import { Box, Flex, Stack, Text } from '@chakra-ui/react';
import { RecipeDetailResponse } from '@diamond/shared/types';
import {
  PlateSerializer,
  RecipeDescriptionCard,
  RecipeImagesSlider,
  RecipeProductTabs,
  RecipeTag,
} from '@diamond/shared/ui';
import { trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { useGetRecipeDetail, useShipToStore } from '@diamond/sol-buyer-context';
import { useRouter } from 'next/router';

import { Image } from '../../common';
import { RecipeCard } from '../RecipeCard';
import { RecipeCartModal } from './cart/RecipeCartModal';
import { useRecipeDetail } from './useRecipeDetail';
import { YoutubePlayer } from './YoutubePlayer';
type RecipeDetailType = {
  data: RecipeDetailResponse;
  onClickHandleFavorite: (id: string) => void;
  isAuthenticated: boolean;
};

export const RecipeDetailView = ({
  data,
  onClickHandleFavorite,
  isAuthenticated,
}: RecipeDetailType) => {
  const router = useRouter();
  const slug = router.query['recipesId'] as string;
  const warehouse = useShipToStore((s) => s.selectedShipToId);
  const { dataUpdatedAt } = useGetRecipeDetail(slug, warehouse);
  const {
    countCartItems,
    getHppPrice,
    isRecommended,
    setRecommended,
    hasAlternativeProducts,
    hasRecommendedProducts,
  } = useRecipeDetail({ data, isAuthenticated });

  return (
    <Flex justifyContent="space-between" gap="4">
      {/* Left Content */}
      <Stack>
        <Box
          w={{ base: '380px', md: '300px', xl: '400px' }}
          h={{ base: '380px', md: '300px', xl: '400px' }}
          flex="none"
          isolation="isolate"
          position="relative"
          borderRadius="lg"
          overflow="hidden"
          mb="4"
        >
          {data.medias ? <RecipeImagesSlider recipe={data} /> : null}
          {data.thumbnail === '' ? (
            <Image
              alt="Recipe Illustration"
              src="/images/product_placeholder.png"
              fill
              style={{ borderRadius: 'inherit', objectFit: 'cover' }}
            />
          ) : null}
        </Box>
        <Box
          borderRadius="lg"
          overflow="hidden"
          p={3}
          bg="white"
          w={{ md: '300px', xl: '400px' }}
        >
          {data.ingredient_groups.length > 0 ? (
            <Box borderRadius="lg" overflow="hidden" p={2} bg="white">
              <Flex p={2} justifyContent="space-between">
                <Text fontSize="xl" fontWeight="bold">
                  Bahan & Produk
                </Text>
                {data.portion_size ? (
                  <RecipeTag
                    tag={{
                      id: '',
                      bg_color: '#FFF2E2',
                      name: `${data.portion_size} Porsi`,
                      text_color: '#A26E2C',
                      big_icon_url: '',
                      small_icon_url: '/images/recipe-icons/cutlery.svg',
                    }}
                  />
                ) : null}
              </Flex>
              <RecipeProductTabs
                data={data}
                isAuthenticated={isAuthenticated}
                hasAlternativeProducts={hasAlternativeProducts}
                hasRecommendedProducts={hasRecommendedProducts}
                onClickRecommended={() => setRecommended(true)}
                onClickAlternative={() => setRecommended(false)}
              />
            </Box>
          ) : null}
        </Box>
      </Stack>

      {/* Main Content */}
      <Stack flex="1" w={{ sm: '380px', xl: '460px' }}>
        <RecipeDescriptionCard
          recipe={data}
          isFavorite={data.is_favorite}
          handleFavorite={onClickHandleFavorite}
          player={
            data.video ? (
              <YoutubePlayer
                videoUrl={data.video}
                handleTrackEvent={() =>
                  trackEvent('click_recipe_tutorial', {
                    video_url: data.video ?? '',
                    id: data.id,
                    name: data.title,
                  })
                }
              />
            ) : null
          }
        />
        <Box borderRadius="lg" overflow="hidden" p={3} bg="white" mt="4">
          {Array.isArray(data.body) && data.body.length > 0 ? (
            <Stack>
              <Box borderRadius="lg" overflow="hidden" p={4} bg="white" pb="0">
                <Flex mb="6" justifyContent="space-between">
                  <Text fontSize={{ md: 'md', xl: 'xl' }} fontWeight="bold">
                    Instruksi Pembuatan
                  </Text>
                  {data.portion_size ? (
                    <RecipeTag
                      tag={{
                        id: '',
                        bg_color: '#D6F4D7',
                        name: `${data.making_duration} Menit`,
                        text_color: '#0D4949',
                        big_icon_url: '',
                        small_icon_url: '/images/recipe-icons/acute.svg',
                      }}
                    />
                  ) : null}
                </Flex>
                <PlateSerializer body={data.body} />
              </Box>
            </Stack>
          ) : null}
        </Box>
        {data.similar_recipes.length > 0 ? (
          <Box>
            <Text fontWeight="bold" fontSize="xl" p={2}>
              Resep Dengan Bahan Serupa
            </Text>
            <Box>
              {data.similar_recipes.map((recipe, i) => {
                return (
                  <RecipeCard
                    key={i}
                    recipe={recipe}
                    handleTrackEvent={(recipe) => {
                      trackEvent(TRACKING_EVENTS.SELECT_RECIPE, {
                        id: recipe.id,
                        name: recipe.title,
                        brand: recipe.brands.toString(),
                        section_name: TRACKING_PAGES.RECIPE,
                      });
                    }}
                    section={TRACKING_PAGES.RECIPE_DETAIL}
                  />
                );
              })}
            </Box>
          </Box>
        ) : null}
      </Stack>

      {/* Right Content */}
      <Box
        w={{ md: '250px', xl: '320px' }}
        position="sticky"
        alignSelf="flex-start"
        top="135px"
      >
        <Box
          bg="white"
          borderRadius="md"
          p={4}
          ml="0px"
          boxShadow="md"
          w={{ md: '250px', xl: '320px' }}
        >
          <Box
            bg="#D9F3FF"
            borderRadius="md"
            p={2}
            mb={3}
            borderWidth="1px"
            borderColor="#A1D1FF"
            bgImage="url('/images/background-hpp.png')"
            backgroundSize={{ base: 'cover', md: '100% 100%' }}
            backgroundPosition="center"
            backgroundRepeat="no-repeat"
          >
            <Text
              fontSize={{ md: 'md', xl: 'xl' }}
              fontWeight="bold"
              color="blue.600"
            >
              Estimasi Total HPP /Porsi
            </Text>
            <Text fontSize={{ md: 'xs', xl: 'sm' }}>
              Berdasarkan produk yang tersedia di Sukanda OneLink
            </Text>
            <Text
              fontSize={{ md: 'xl', xl: '2xl' }}
              fontWeight="bold"
              color="#006BD1"
              mt={2}
              mb={4}
            >
              {getHppPrice()}
            </Text>
          </Box>
          <RecipeCartModal
            key={`${slug}${dataUpdatedAt}${isRecommended}`}
            data={data}
            countItems={countCartItems()}
            isDisabled={
              isRecommended ? !hasRecommendedProducts : !hasAlternativeProducts
            }
            isRecommended={isRecommended}
          />
        </Box>
      </Box>
    </Flex>
  );
};
